import { FC, useState, useRef, useEffect } from 'react';

import { useNovaPoshtaSearch } from '../../hooks/useNovaPoshtaSearch';
import { Routes } from '../../models/routes';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

interface Office {
    ref: string;
    index: string;
    short_address: string;
    name: string;
}

interface Props {
    settlementRef: string;
    value: string;
    onSelect: (office: {
        ref: string;
        index: string;
        address: string;
        name: string;
    }) => void;
    onFallbackInput: (value: string) => void;
    error?: string;
    isFallback?: boolean;
}

export const UkrposhtaOfficeSelect: FC<Props> = ({
    settlementRef,
    value,
    onSelect,
    onFallbackInput,
    error,
    isFallback: externalFallback = false,
}) => {
    const [query, setQuery] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const hasInitializedReset = useRef(false);

    const { results, isFallback, search, clear } = useNovaPoshtaSearch<Office>({
        url: Routes.ApiUkrposhtaOffices,
        params: { settlement_ref: settlementRef },
        minLength: 0,
        enabled: !!settlementRef && !externalFallback,
    });

    const useFallback = externalFallback || isFallback;

    useEffect(() => {
        setQuery(value);
    }, [value]);

    useEffect(() => {
        if (!hasInitializedReset.current) {
            hasInitializedReset.current = true;
            return;
        }

        setQuery('');
        clear();
    }, [settlementRef, clear]);

    // Load initial list when settlementRef is available
    useEffect(() => {
        if (settlementRef && !useFallback) {
            search('');
        }
    }, [settlementRef]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (val: string) => {
        setQuery(val);
        if (useFallback) {
            onFallbackInput(val);
        } else {
            search(val);
            setIsOpen(true);
        }
    };

    const handleSelect = (office: Office) => {
        const displayText = office.index
            ? `${office.index} — ${office.short_address}`
            : office.short_address;
        setQuery(displayText);
        setIsOpen(false);
        clear();
        onSelect({
            ref: office.ref,
            index: office.index,
            address: office.short_address,
            name: office.name,
        });
    };

    const handleFocus = () => {
        if (!useFallback && results.length > 0) {
            setIsOpen(true);
        } else if (!useFallback && settlementRef) {
            search(query);
            setIsOpen(true);
        }
    };

    return (
        <div ref={wrapperRef} className="relative space-y-1">
            <Label htmlFor="up_office">Відділення *</Label>
            <Input
                id="up_office"
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={handleFocus}
                placeholder={
                    useFallback
                        ? 'Введіть назву або індекс відділення...'
                        : 'Пошук за індексом, адресою або назвою...'
                }
                className={error ? 'border-red-500' : ''}
                autoComplete="off"
                disabled={!settlementRef && !useFallback}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}

            {isOpen && results.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-white shadow-lg dark:border-white/10 dark:bg-slate-900 dark:shadow-2xl">
                    {results.map((office) => (
                        <li
                            key={office.ref}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50 dark:text-slate-100 dark:hover:bg-slate-800"
                            onClick={() => handleSelect(office)}
                        >
                            {office.index && (
                                <span className="font-medium">
                                    {office.index}
                                </span>
                            )}
                            <span className="ml-2 text-xs text-muted-foreground">
                                {office.short_address}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
